import { Container } from 'react-bootstrap'
import { getIntegrationDisplayName, IntegrationServiceId } from '../../../../../../../common/integrations'
import { AdditionalFieldSelector } from '../AdditionalFieldSelector'
import { FieldReferenceFieldsPicker } from '../shared/FieldReferenceFieldsPicker'
import { ContactLinking } from '../../ContactLinking/ContactLinking'
import { IntegrationMappingFilterSection } from '../IntegrationMappingFilterSection'
import { useFormContext } from 'react-hook-form'
import { IntegrationSettings } from '../../../schema'
import { useMemo } from 'react'
import { CustomObjectMapping } from '../../../../../../../models/Account'

const MAX_REFERENCE_FIELDS = 3

interface CustomObjectBodyProps {
  serviceId: IntegrationServiceId
  objectIndex: number
}

export const CustomObjectBody: React.FC<CustomObjectBodyProps> = ({ serviceId, objectIndex }) => {
  const { watch, setValue } = useFormContext<IntegrationSettings>()
  const customObjects = watch('mappings.customObjects') as CustomObjectMapping[] | undefined
  const currentObject = useMemo(() => customObjects?.[objectIndex], [customObjects, objectIndex])
  const alreadyAddedFieldKeys = useMemo(() => (currentObject?.fields ?? []).map((f) => f.objectName), [currentObject])

  const handleReferenceFieldsChange = (referenceFields: string[]) => {
    const updatedObjects = [...(customObjects ?? [])]
    updatedObjects[objectIndex] = { ...updatedObjects[objectIndex], referenceFields }
    setValue('mappings.customObjects', updatedObjects, { shouldDirty: true, shouldValidate: true })
  }

  return (
    <Container className='py-3'>
      <div className='mb-5'>
        <AdditionalFieldSelector
          title='Add field'
          description={`Choose which ${getIntegrationDisplayName(serviceId)} fields to include. This helps your AI Assistant understand the data better.`}
          serviceId={serviceId}
          mappingObjectName='customObjects'
          mappingObjectIndex={objectIndex}
          alreadyAddedFields={alreadyAddedFieldKeys}
          fieldsShape='array'
        />
      </div>
      <div className='mb-5'>
        {currentObject?.objectName ? (
          <FieldReferenceFieldsPicker
            serviceId={serviceId}
            referenceObjectName={currentObject.objectName}
            referenceFields={currentObject?.referenceFields ?? []}
            onChange={handleReferenceFieldsChange}
            maxFields={MAX_REFERENCE_FIELDS}
            alwaysExpanded
            title='Reference field'
            description={`Pick the field that best identifies a record (e.g. its name). Zocks uses it to label ${currentObject.objectName} records when listing them during a CRM update. You can add up to ${MAX_REFERENCE_FIELDS} fields.`}
          />
        ) : null}
      </div>
      <div className='mb-5'>
        <ContactLinking
          serviceId={serviceId}
          zocksObjectName={currentObject?.objectName || 'Custom Object'}
          mappingPath={`mappings.customObjects.${objectIndex}`}
          showHouseholdCheckbox={false}
          allowClearing
        />
      </div>
      <div className='mb-4'>
        <IntegrationMappingFilterSection
          serviceId={serviceId}
          mappingKey='customObjects'
          mappingObjectIndex={objectIndex}
          title='Create filter'
          description='Apply filters to limit which records Zocks references from this object.'
        />
      </div>
    </Container>
  )
}
